import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';

import { Stack, Text } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import {
  PATIENT_BY_IDENTIFIER_QUERY,
  PATIENT_BY_IDENTIFIER_QUERY_SOURCE,
  useMedplum,
} from '@eluve/medplum-client';

const ACCESS_NOTE_KEYS = {
  'eluve-admin': {
    label: 'patients.medicalProfile.accessEluveAdminLabel',
    detail: 'patients.medicalProfile.accessEluveAdminDetail',
  },
  'tenant-admin': {
    label: 'patients.medicalProfile.accessTenantAdminLabel',
    detail: 'patients.medicalProfile.accessTenantAdminDetail',
  },
  'tenant-member': {
    label: 'patients.medicalProfile.accessTenantMemberLabel',
    detail: 'patients.medicalProfile.accessTenantMemberDetail',
  },
} as const satisfies Record<
  'eluve-admin' | 'tenant-admin' | 'tenant-member',
  { label: string; detail: string }
>;

/**
 * Always-on header for the Medical Profile: the access banner explaining why
 * this FHIR record is (or isn't) visible. The data views — Care Team,
 * Observations, Patient Uploads — render below it via the sub-tab Outlet.
 */
export const PatientProfile: React.FC = () => {
  const { t } = useTranslation();
  const { patientId } = useParams() as { patientId: string };
  const { fhirClient, access } = useMedplum();

  const { data: patientData, loading: isPatientLoading } = useQuery(
    PATIENT_BY_IDENTIFIER_QUERY,
    {
      client: fhirClient ?? undefined,
      variables: { identifier: patientId },
      skip: !fhirClient,
      fetchPolicy: 'cache-and-network',
    },
  );

  const patient = patientData?.PatientList?.[0] ?? null;
  const hasAccess = Boolean(patient);

  if (isPatientLoading && !patientData) {
    return <Text>{t('patients.common.checkingAccess')}</Text>;
  }

  const accessNote = access ? ACCESS_NOTE_KEYS[access.reason] : null;

  if (!hasAccess) {
    return (
      <Stack gap={3} className="pt-2">
        <div className="rounded-md border border-dashed p-4">
          <Stack gap={2}>
            <Text weight="medium">
              {t('patients.medicalProfile.noFhirPatientTitle')}
            </Text>
            <Text size="sm" color="tertiary">
              The GraphQL query below returned an empty list. This happens when
              either (a) the FHIR server's AccessPolicy scoped to your profile
              filters the Patient out — tenant-members see only patients where
              they are listed as the general practitioner — or (b) the Patient
              has not been synced into the FHIR store yet. The client cannot
              distinguish the two cases; both look like an empty list.
            </Text>
            <Text size="sm" color="tertiary">
              Operation executed against{' '}
              <code className="font-mono text-xs">/fhir/R4/$graphql</code>:
            </Text>
            <pre className="overflow-x-auto rounded-sm bg-bg-tertiary p-3 text-xs/relaxed">
              <code className="font-mono whitespace-pre">
                {PATIENT_BY_IDENTIFIER_QUERY_SOURCE}
              </code>
            </pre>
            <pre className="overflow-x-auto rounded-sm bg-bg-tertiary p-3 text-xs">
              <code className="font-mono">
                {'// variables\n'}
                {JSON.stringify({ identifier: patientId }, null, 2)}
              </code>
            </pre>
          </Stack>
        </div>
      </Stack>
    );
  }

  if (!accessNote) return null;

  return (
    <div className="rounded-md border border-border-secondary bg-bg-secondary px-3 py-2">
      <Stack gap={0}>
        <Text size="xs" weight="medium">
          {t(accessNote.label)}
        </Text>
        <Text size="xs" color="tertiary">
          {t(accessNote.detail)}
        </Text>
      </Stack>
    </div>
  );
};
